import { useMemo, useState } from "react";
import { ClipboardCheck, Clock, History, ListOrdered, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LinkOrderDialog } from "@/components/LinkOrderDialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import {
  useActionFinancialAidOrder,
  useActionFinancialAidOrderDecision,
} from "@/features/financial-aids/api/hooks";
import {
  useActionToolOrder,
  useActionToolOrderDecision,
} from "@/features/tools/api/hooks";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import {
  CatalogueWriteError,
  useCatalogueList,
  useFlagRequests,
  useSaveItem,
  useStageFlag,
} from "../api";
import type { CatalogueItem, CatalogueKind, FlagKey } from "../types";
import type { CatalogueDialogs } from "./dialogs";
import { FlagReviewDialog } from "./FlagReviewDialog";
import { RowActions } from "./RowActions";

export type DashboardTabKey = "issues" | "goals" | "actions";

const KIND: Record<DashboardTabKey, CatalogueKind> = {
  issues: "issue",
  goals: "goal",
  actions: "action",
};
const FLAG: Partial<Record<DashboardTabKey, { key: FlagKey; field: "can_have_experts" | "available_in_demo" }>> = {
  actions: { key: "expert-access", field: "can_have_experts" },
  goals: { key: "demo", field: "available_in_demo" },
};

interface Draft {
  name_fr?: string;
  name_en?: string;
  flag?: boolean;
}

const selectClass =
  "h-9 rounded-md border border-input bg-background px-2 text-sm";

/**
 * The action, goal and issue dashboards — the SPA side of the Django pages of
 * the same names: the tree by thematique › enjeu › objectif, the filter bar,
 * FR/EN renaming saved as a batch, the usage counts, and for actions and goals
 * the flag that only a second administrator can apply.
 */
export function DashboardTab({
  tab,
  dialogs,
}: {
  tab: DashboardTabKey;
  dialogs: CatalogueDialogs;
}) {
  const { t } = useTranslation();
  const kind = KIND[tab];
  const flag = FLAG[tab];

  const thematics = useCatalogueList("thematic");
  const issues = useCatalogueList("issue");
  const goals = useCatalogueList("goal", tab !== "issues");
  const rowsQuery = useCatalogueList(kind);
  const requests = useFlagRequests(flag?.key ?? "expert-access");
  const save = useSaveItem(kind);
  const stage = useStageFlag(flag?.key ?? "expert-access");

  const [filters, setFilters] = useState({ thematic: "", issue: "", goal: "", english: "", q: "" });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [errors, setErrors] = useState<Record<string, Record<string, string[]>>>({});
  const [reviewing, setReviewing] = useState(false);
  const [order, setOrder] = useState<{ type: "tool" | "aid"; action: CatalogueItem } | null>(null);

  const issueById = useMemo(
    () => new Map((issues.data ?? []).map((row) => [row.id, row])),
    [issues.data],
  );
  const goalById = useMemo(
    () => new Map((goals.data ?? []).map((row) => [row.id, row])),
    [goals.data],
  );
  const thematicById = useMemo(
    () => new Map((thematics.data ?? []).map((row) => [row.id, row])),
    [thematics.data],
  );

  /** The row's place in the hierarchy: its thematic, issue and goal ids. */
  const place = (row: CatalogueItem) => {
    if (tab === "issues") return { thematic: row.parent_id ?? "", issue: row.id, goal: "" };
    if (tab === "goals") {
      const issue = issueById.get(row.parent_id ?? "");
      return { thematic: issue?.parent_id ?? "", issue: row.parent_id ?? "", goal: row.id };
    }
    const goal = goalById.get(row.parent_id ?? "");
    const issue = issueById.get(goal?.parent_id ?? "");
    return { thematic: issue?.parent_id ?? "", issue: goal?.parent_id ?? "", goal: row.parent_id ?? "" };
  };

  const rows = useMemo(() => rowsQuery.data ?? [], [rowsQuery.data]);
  const siblingsCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) counts.set(row.parent_id ?? "", (counts.get(row.parent_id ?? "") ?? 0) + 1);
    return counts;
  }, [rows]);

  const draftOf = (row: CatalogueItem): Required<Draft> => ({
    name_fr: drafts[row.id]?.name_fr ?? row.name_fr,
    name_en: drafts[row.id]?.name_en ?? row.name_en,
    flag: drafts[row.id]?.flag ?? Boolean(flag && row[flag.field]),
  });
  const isDirty = (row: CatalogueItem) => {
    const draft = draftOf(row);
    return (
      draft.name_fr !== row.name_fr ||
      draft.name_en !== row.name_en ||
      (flag ? draft.flag !== Boolean(row[flag.field]) : false)
    );
  };
  const dirtyRows = rows.filter(isDirty);

  const q = filters.q.trim().toLowerCase();
  const visible = rows.filter((row) => {
    const where = place(row);
    if (filters.thematic && where.thematic !== filters.thematic) return false;
    if (filters.issue && where.issue !== filters.issue) return false;
    if (filters.goal && where.goal !== filters.goal) return false;
    if (filters.english === "missing" && row.name_en.trim()) return false;
    if (filters.english === "has" && !row.name_en.trim()) return false;
    if (q && !`${row.name_fr} ${row.name_en}`.toLowerCase().includes(q)) return false;
    return true;
  });

  // Thematic › issue › goal buckets, in the order the API lists them.
  const tree = (() => {
    const out: {
      thematic: CatalogueItem;
      issues: { issue: CatalogueItem | null; goals: { goal: CatalogueItem | null; rows: CatalogueItem[] }[] }[];
    }[] = [];
    for (const row of visible) {
      const where = place(row);
      const thematic = thematicById.get(where.thematic);
      if (!thematic) continue;
      let tBucket = out.find((b) => b.thematic.id === thematic.id);
      if (!tBucket) {
        tBucket = { thematic, issues: [] };
        out.push(tBucket);
      }
      const issueKey = tab === "issues" ? "" : where.issue;
      let iBucket = tBucket.issues.find((b) => (b.issue?.id ?? "") === issueKey);
      if (!iBucket) {
        iBucket = { issue: issueKey ? issueById.get(issueKey) ?? null : null, goals: [] };
        tBucket.issues.push(iBucket);
      }
      const goalKey = tab === "actions" ? where.goal : "";
      let gBucket = iBucket.goals.find((b) => (b.goal?.id ?? "") === goalKey);
      if (!gBucket) {
        gBucket = { goal: goalKey ? goalById.get(goalKey) ?? null : null, rows: [] };
        iBucket.goals.push(gBucket);
      }
      gBucket.rows.push(row);
    }
    return out;
  })();

  const reviews = (requests.data ?? []).filter((req) => !req.mine);

  const setDraft = (row: CatalogueItem, patch: Draft) =>
    setDrafts((current) => ({ ...current, [row.id]: { ...current[row.id], ...patch } }));

  const saveAll = async () => {
    const nextErrors: typeof errors = {};
    let saved = 0;
    let staged = 0;
    for (const row of dirtyRows) {
      const draft = draftOf(row);
      try {
        if (draft.name_fr !== row.name_fr || draft.name_en !== row.name_en) {
          await save.mutateAsync({
            id: row.id,
            body: { name_fr: draft.name_fr, name_en: draft.name_en },
          });
        }
        if (flag && draft.flag !== Boolean(row[flag.field])) {
          const result = await stage.mutateAsync({ targetId: row.id, value: draft.flag });
          if (result?.status === "staged") staged += 1;
        }
        saved += 1;
      } catch (error) {
        nextErrors[row.id] =
          error instanceof CatalogueWriteError && error.errors
            ? error.errors
            : { name_fr: [t("catalogue.error")] };
      }
    }
    setErrors(nextErrors);
    setDrafts((current) =>
      Object.fromEntries(Object.entries(current).filter(([id]) => id in nextErrors)),
    );
    if (saved) {
      // The Django dashboards' flash, sentence for sentence.
      const parts = [t(`catalogue.dashboard.savedCount.${tab}`, { n: saved })];
      if (staged) parts.push(t(`catalogue.dashboard.stagedCount.${tab}`, { n: staged }));
      toast.success(parts.join(" "));
    }
  };

  if (rowsQuery.isPending || thematics.isPending || issues.isPending) {
    return <p className="p-4 text-sm text-muted-foreground">{t("catalogue.loading")}</p>;
  }
  if (rowsQuery.isError) {
    return <p role="alert" className="p-4 text-sm text-destructive-ink">{t("catalogue.error")}</p>;
  }

  const issueOptions = (issues.data ?? []).filter(
    (row) => !filters.thematic || row.parent_id === filters.thematic,
  );
  const goalOptions = (goals.data ?? []).filter((row) => {
    if (filters.issue) return row.parent_id === filters.issue;
    if (filters.thematic) return issueById.get(row.parent_id ?? "")?.parent_id === filters.thematic;
    return true;
  });

  const countKey = filters.thematic || filters.issue || filters.goal || filters.english || q
    ? `catalogue.dashboard.count.${tab}Filtered`
    : `catalogue.dashboard.count.${tab}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          {t(`catalogue.dashboard.subtitle.${tab}`)}
        </p>
        {flag && (
          <Button
            variant={reviews.length ? "default" : "outline"}
            disabled={!reviews.length}
            title={
              reviews.length
                ? t("catalogue.review.validatePending")
                : t("catalogue.review.validateNone")
            }
            onClick={() => setReviewing(true)}
          >
            <ClipboardCheck aria-hidden />
            {t("catalogue.review.validate")}
            {reviews.length > 0 && <span className="font-bold">{reviews.length}</span>}
          </Button>
        )}
        <Button variant="outline" onClick={() => dialogs.openRecent(kind)}>
          <History aria-hidden />
          {t("catalogue.recent")}
        </Button>
        <Button onClick={() => dialogs.openAdd(kind)}>
          <Plus aria-hidden />
          {t(`catalogue.addTitle.${kind}`)}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-3">
        <label className="flex flex-col gap-1 text-xs font-medium">
          {t("catalogue.dashboard.filters.thematic")}
          <select
            className={selectClass}
            value={filters.thematic}
            onChange={(e) => setFilters({ ...filters, thematic: e.target.value, issue: "", goal: "" })}
          >
            <option value="">{t("catalogue.dashboard.filters.allFeminine")}</option>
            {(thematics.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </label>
        {tab !== "issues" && (
          <label className="flex flex-col gap-1 text-xs font-medium">
            {t("catalogue.dashboard.filters.issue")}
            <select
              className={selectClass}
              value={filters.issue}
              onChange={(e) => setFilters({ ...filters, issue: e.target.value, goal: "" })}
            >
              <option value="">{t("catalogue.dashboard.filters.allMasculine")}</option>
              {issueOptions.map((row) => (
                <option key={row.id} value={row.id}>{row.name}</option>
              ))}
            </select>
          </label>
        )}
        {tab === "actions" && (
          <label className="flex flex-col gap-1 text-xs font-medium">
            {t("catalogue.dashboard.filters.goal")}
            <select
              className={selectClass}
              value={filters.goal}
              onChange={(e) => setFilters({ ...filters, goal: e.target.value })}
            >
              <option value="">{t("catalogue.dashboard.filters.allMasculine")}</option>
              {goalOptions.map((row) => (
                <option key={row.id} value={row.id}>{row.name}</option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-xs font-medium">
          {t("catalogue.dashboard.filters.english")}
          <select
            className={selectClass}
            value={filters.english}
            onChange={(e) => setFilters({ ...filters, english: e.target.value })}
          >
            <option value="">{t("catalogue.dashboard.filters.allFeminine")}</option>
            <option value="missing">{t("catalogue.dashboard.filters.missing")}</option>
            <option value="has">{t("catalogue.dashboard.filters.present")}</option>
          </select>
        </label>
        <label className="relative flex min-w-48 flex-1 flex-col gap-1 text-xs font-medium">
          <span className="sr-only">{t(`catalogue.dashboard.search.${tab}`)}</span>
          <Search aria-hidden className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
          <input
            type="search"
            className={cn(selectClass, "pl-8")}
            placeholder={t(`catalogue.dashboard.search.${tab}`)}
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          />
        </label>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFilters({ thematic: "", issue: "", goal: "", english: "", q: "" })}
        >
          {t("catalogue.dashboard.filters.reset")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t(countKey, { count: visible.length, total: rows.length })}
      </p>
      <div role="note" className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
        <strong>{t("catalogue.dashboard.bannerStrong")}</strong>{" "}
        {t(`catalogue.dashboard.banner.${tab}`)}
      </div>

      {tree.length === 0 && (
        <p className="text-sm text-muted-foreground">{t(`catalogue.dashboard.noMatch.${tab}`)}</p>
      )}
      {tree.map(({ thematic, issues: issueBuckets }) => {
        const theme = thematicClasses(thematic.css_class);
        return (
          <details key={thematic.id} open className="rounded-md border border-border">
            <summary className={cn("flex cursor-pointer items-center gap-2 px-3 py-2 font-semibold", theme.softBg, theme.ink)}>
              <span aria-hidden className={cn("size-2.5 rounded-full", theme.solid)} />
              {thematic.name}
              {tab === "issues" && (
                <AddHere onClick={() => dialogs.openAdd(kind, { thematic: thematic.id })} />
              )}
            </summary>
            <div className="space-y-3 p-3">
              {issueBuckets.map(({ issue, goals: goalBuckets }) => (
                <div key={issue?.id ?? "all"} className="space-y-3">
                  {issue && (
                    <h3 className="flex items-center gap-2 text-sm font-semibold">
                      {issue.name}
                      {tab === "goals" && (
                        <AddHere onClick={() => dialogs.openAdd(kind, { issue: issue.id })} />
                      )}
                    </h3>
                  )}
                  {goalBuckets.map(({ goal, rows: cards }) => (
                    <div key={goal?.id ?? "all"} className="space-y-2">
                      {goal && (
                        <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                          {t("catalogue.dashboard.filters.goal")} · {goal.name}
                          <AddHere onClick={() => dialogs.openAdd(kind, { goal: goal.id })} />
                        </p>
                      )}
                      <div className="grid gap-3">
                        {cards.map((row) => {
                          const draft = draftOf(row);
                          const rowErrors = errors[row.id] ?? {};
                          const pending = row.pending_flag;
                          const toolsOn = Boolean(thematic.tools_enabled);
                          const aidsOn = Boolean(thematic.financial_aids_enabled);
                          const siblings = siblingsCount.get(row.parent_id ?? "") ?? 0;
                          return (
                            <article
                              key={row.id}
                              data-catalogue-card={row.id}
                              className={cn(
                                "rounded-md border bg-card p-3",
                                isDirty(row) ? "border-primary" : "border-border",
                              )}
                            >
                              <div className="grid gap-3 md:grid-cols-2">
                                <label className="space-y-1 text-xs font-medium">
                                  <span>
                                    <span className="mr-1 rounded bg-muted px-1">FR</span>
                                    {t(`catalogue.dashboard.text.${tab}`)}
                                  </span>
                                  <textarea
                                    rows={2}
                                    className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm font-normal"
                                    value={draft.name_fr}
                                    onChange={(e) => setDraft(row, { name_fr: e.target.value })}
                                  />
                                  {rowErrors.name_fr?.map((m) => (
                                    <span key={m} className="block text-destructive-ink">{m}</span>
                                  ))}
                                </label>
                                <label className="space-y-1 text-xs font-medium">
                                  <span>
                                    <span className="mr-1 rounded bg-muted px-1">EN</span>
                                    {t("catalogue.dashboard.textEn")}
                                  </span>
                                  <textarea
                                    rows={2}
                                    className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm font-normal"
                                    value={draft.name_en}
                                    onChange={(e) => setDraft(row, { name_en: e.target.value })}
                                  />
                                  {rowErrors.name_en?.map((m) => (
                                    <span key={m} className="block text-destructive-ink">{m}</span>
                                  ))}
                                </label>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                                {flag && (
                                  <span className="inline-flex items-center gap-2">
                                    <Switch
                                      checked={draft.flag}
                                      disabled={Boolean(pending)}
                                      onCheckedChange={(next) => setDraft(row, { flag: next })}
                                      label={
                                        tab === "actions"
                                          ? t("catalogue.dashboard.canHaveExperts")
                                          : t("catalogue.dashboard.availableInDemo")
                                      }
                                    />
                                    {tab === "actions"
                                      ? t("catalogue.dashboard.canHaveExperts")
                                      : t("catalogue.dashboard.availableInDemo")}
                                  </span>
                                )}
                                {pending && (
                                  <span
                                    title={t("catalogue.dashboard.pendingTitle")}
                                    className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-warning-ink"
                                  >
                                    <Clock aria-hidden className="size-3" />
                                    {t("catalogue.dashboard.pending")}:{" "}
                                    {pending.proposed_value
                                      ? t("catalogue.dashboard.on")
                                      : t("catalogue.dashboard.off")}
                                  </span>
                                )}
                                {isDirty(row) && (
                                  <span className="font-semibold text-primary">
                                    {t("catalogue.dashboard.modified")}
                                  </span>
                                )}
                                {!row.name_en.trim() && (
                                  <span className="text-warning-ink">{t("catalogue.missingTranslation")}</span>
                                )}
                                {row.counts && (
                                  <span className="text-muted-foreground">
                                    <b>{row.counts.plans}</b> {t("catalogue.dashboard.plans")} ·{" "}
                                    <b>{row.counts.communications}</b> {t("catalogue.dashboard.communications")} ·{" "}
                                    <b>{row.counts.experts}</b> {t("catalogue.dashboard.experts")}
                                  </span>
                                )}
                                {tab === "actions" &&
                                  toolsOn &&
                                  ((row.counts?.tool_links ?? 0) > 1 || row.tool_order_state) && (
                                    <OrderButton
                                      label={`${t("catalogue.dashboard.tools")} (${row.counts?.tool_links ?? 0})`}
                                      title={t("catalogue.dashboard.toolOrderTitle")}
                                      state={row.tool_order_state}
                                      onClick={() => setOrder({ type: "tool", action: row })}
                                    />
                                  )}
                                {tab === "actions" &&
                                  aidsOn &&
                                  ((row.counts?.financial_aid_links ?? 0) > 1 ||
                                    row.financial_aid_order_state) && (
                                    <OrderButton
                                      label={`${t("catalogue.dashboard.aids")} (${row.counts?.financial_aid_links ?? 0})`}
                                      title={t("catalogue.dashboard.aidOrderTitle")}
                                      state={row.financial_aid_order_state}
                                      onClick={() => setOrder({ type: "aid", action: row })}
                                    />
                                  )}
                              </div>
                              <div className="mt-2 border-t border-border pt-2">
                                <RowActions
                                  kind={kind}
                                  item={row}
                                  movable
                                  isFirst={row.position === 1}
                                  isLast={row.position === siblings}
                                  onEdit={() => dialogs.openEdit(kind, row)}
                                  onDelete={() => dialogs.openDelete(kind, row)}
                                  onHistory={() => dialogs.openHistory(kind, row)}
                                />
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </details>
        );
      })}

      {dirtyRows.length > 0 && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 rounded-md border border-border bg-background p-3 shadow-md">
          <span className="text-sm">
            <b>{dirtyRows.length}</b> {t(`catalogue.dashboard.dirty.${tab}`)}
          </span>
          <span className="flex-1" />
          <Button variant="outline" onClick={() => { setDrafts({}); setErrors({}); }}>
            {t("catalogue.cancel")}
          </Button>
          <Button onClick={() => void saveAll()} disabled={save.isPending || stage.isPending}>
            {t("catalogue.dashboard.saveChanges")}
          </Button>
        </div>
      )}

      {reviewing && flag && (
        <FlagReviewDialog flag={flag.key} requests={reviews} onClose={() => setReviewing(false)} />
      )}
      {order?.type === "tool" && (
        <LinkOrderDialog
          action={{ id: order.action.id, name: order.action.name }}
          ns="tools"
          useOrder={useActionToolOrder}
          useDecision={useActionToolOrderDecision}
          onClose={() => setOrder(null)}
        />
      )}
      {order?.type === "aid" && (
        <LinkOrderDialog
          action={{ id: order.action.id, name: order.action.name }}
          ns="financialAids"
          useOrder={useActionFinancialAidOrder}
          useDecision={useActionFinancialAidOrderDecision}
          onClose={() => setOrder(null)}
        />
      )}
    </div>
  );
}

function AddHere({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="ml-auto h-7"
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      <Plus aria-hidden />
      {t("catalogue.add")}
    </Button>
  );
}

function OrderButton({
  label,
  title,
  state,
  onClick,
}: {
  label: string;
  title: string;
  state?: string | null;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1">
      <Button type="button" variant="outline" size="sm" className="h-7" title={title} onClick={onClick}>
        <ListOrdered aria-hidden />
        {label}
      </Button>
      {state && (
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5",
            state === "mine"
              ? "border-border bg-muted text-muted-foreground"
              : "border-warning/30 bg-warning/10 text-warning-ink",
          )}
        >
          <Clock aria-hidden className="size-3" />
          {state === "mine" ? t("tools.linkOrder.pending") : t("tools.linkOrder.toValidate")}
        </span>
      )}
    </span>
  );
}
